import { fetchAuthSession } from 'aws-amplify/auth';
import type { AsnFailures, AsnFailuresQuery, AsnMetrics, AsnMetricsService } from '../types';
import { getAwsConfig } from '../../config/appConfig';
import { ensureAmplifyConfigured } from './amplifyConfig';

/**
 * Resolve the API base, a bearer token, and the caller's Cognito identity-pool
 * id, or throw with a clear message. The identityId is the value the submission
 * channels record on each row (File Drop keys S3 by it), so metrics reads scope
 * to the same identity the writes used.
 */
async function resolveApi(): Promise<{ base: string; token: string; identityId?: string }> {
    ensureAmplifyConfigured();

    const base = getAwsConfig().asnApiEndpoint;
    if (!base) {
        throw new Error('ASN API endpoint is not configured.');
    }

    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
        throw new Error('Not authenticated.');
    }
    return { base: base.replace(/\/$/, ''), token, identityId: session.identityId };
}

/**
 * ASN metrics from the service API (GET /v1/asn/metrics). Returns per-
 * integration counts (total / data errors / business errors) for the window,
 * scoped to the signed-in viewer (their Cognito identity-pool id).
 */
export const awsAsnMetricsService: AsnMetricsService = {
    async getMetrics(from?: string, to?: string): Promise<AsnMetrics> {
        const { base, token, identityId } = await resolveApi();

        // Scope to the viewer's own identity (the same id the submission
        // channels record), so a supplier sees exactly their own metrics.
        const url = new URL(`${base}/v1/asn/metrics`);
        if (from) url.searchParams.set('from', from);
        if (to) url.searchParams.set('to', to);
        if (identityId) url.searchParams.set('identityId', identityId);

        const res = await fetch(url.toString(), {
            headers: { Authorization: token },
        });
        if (!res.ok) {
            throw new Error(`Failed to load ASN metrics: ${res.status} ${res.statusText}`);
        }
        return (await res.json()) as AsnMetrics;
    },

    async getFailures(query: AsnFailuresQuery): Promise<AsnFailures> {
        const { base, token, identityId } = await resolveApi();

        const url = new URL(`${base}/v1/asn/failures`);
        url.searchParams.set('source', query.source);
        url.searchParams.set('bucket', query.bucket);
        if (query.from) url.searchParams.set('from', query.from);
        if (query.to) url.searchParams.set('to', query.to);
        // Scope to the viewer's own identity-pool id (same value the metrics
        // read and the File Drop writes use), unless an explicit login filter
        // is supplied (ops viewing a specific supplier). The backend also adds
        // the viewer's resolved M2M clientId so the drill-down matches the
        // counts across both channels.
        const scopedIdentity = query.identityId ?? identityId;
        if (scopedIdentity) url.searchParams.set('identityId', scopedIdentity);
        if (query.limit) url.searchParams.set('limit', String(query.limit));
        if (query.cursor) url.searchParams.set('cursor', query.cursor);

        const res = await fetch(url.toString(), {
            headers: { Authorization: token },
        });
        if (!res.ok) {
            throw new Error(`Failed to load failures: ${res.status} ${res.statusText}`);
        }
        return (await res.json()) as AsnFailures;
    },
};

export default awsAsnMetricsService;
